import { inject } from "@angular/core";
import { Router, type CanActivateFn, type UrlTree } from "@angular/router";

import { TENANT_CONTEXT } from "../auth/tenant-context.token";
import { AuthStateService } from "../services/auth-state.service";

/**
 * The Entra door is admin-only. Everyone who comes through it must hold the
 * Davaco Admin role; anyone who does not is sent to /no-access.
 *
 * Why the role is an ENTRY ticket here rather than an elevation: a staff token
 * carries no organization claim, so `resolvePortalScope` has nothing to scope a
 * Davaco employee by. Its three cases are a customer token, an employee viewing
 * as a customer, and the admin view — and a role-less employee falls through
 * all of them, which the API answers with 403 on every data route. Before this
 * guard existed they still reached /dashboard and saw a shell in which every
 * panel failed. Bouncing them is not a new restriction; it is the same refusal,
 * said once and legibly.
 *
 * Auth0 sessions are returned untouched. A customer is authorized by their
 * `org_name` claim, checked by orgGuard here and re-decided from the verified
 * token by customer-portal-api — the Entra app role is not part of that path
 * and requiring it would lock out every customer. A Davaco employee arriving on
 * an Auth0 token holds the role under its Auth0 spelling ("Davaco - Admin"),
 * which `isDavacoAdmin` already accepts.
 *
 * Keyed on the tenant context rather than the token's issuer because the two
 * agree by construction: AUTH_PROVIDER is selected from this same value, so
 * `kind === "entra"` IS the MSAL session. It also reads synchronously, which a
 * guard needs.
 *
 * A UI affordance, like every guard here. customer-portal-api re-checks the
 * role against the verified token on every request it serves.
 */
export const staffAccessGuard: CanActivateFn = (): boolean | UrlTree => {
  const tenant = inject(TENANT_CONTEXT);
  const authState = inject(AuthStateService);
  const router = inject(Router);

  if (tenant.kind !== "entra") {
    return true;
  }

  // The API's OWN verdict, not the union in `isDavacoAdmin`. The union is right
  // for RENDERING (a nav item may appear on either authority) and wrong for
  // ENTRY, because the two authorities are not interchangeable: an employee
  // assigned on the SPA registration alone carries the role in the id token,
  // unions to true, and would be admitted to a shell in which every data
  // request 403s — the precise failure this guard exists to remove. Only the
  // API's answer predicts whether the data loads.
  const serverVerdict: boolean | null = authState.serverIsDavacoAdmin();
  if (serverVerdict !== null) {
    return serverVerdict ? true : router.createUrlTree(["/no-access"]);
  }

  // /portal/me never answered — it failed, or the bootstrap cleared the session.
  // Fall back to the token claims rather than refusing, because the two states
  // are not the same and this screen can only honestly report one of them: an
  // unreachable API means we cannot prove the role is absent, and telling an
  // employee they lack a role when the server is simply down sends them to an
  // administrator who will find nothing wrong. A held token still fails at the
  // API, visibly, which is the accurate symptom of an outage.
  return authState.isDavacoAdmin() ? true : router.createUrlTree(["/no-access"]);
};
